/**
 * @file api.ts
 * @brief Company Admin → scoped sessions (same `/api/sessions` as mobile).
 *
 * @author DES
 * @date 19-06-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiDelete, apiGet } from "@/shared/api/client";
import { sessionEndpoints } from "@/shared/api/endpoints";
import {
  AdminSessionDetailSchema,
  type AdminSessionDetail,
} from "@/features/sessions/schemas";
import {
  CompanySessionDetailResponseSchema,
  CompanySessionsResponseSchema,
  type CompanySessionsListParams,
  type CompanySessionsResponse,
} from "./schemas";

export const companySessionsKeys = {
  all: ["company", "sessions"] as const,
  list: (params: CompanySessionsListParams) =>
    ["company", "sessions", "list", params] as const,
  detail: (id: string) => ["company", "sessions", "detail", id] as const,
};

/** One owner in the grouped company list (`?group=employee`, 01-10-2026). */
export const SessionsEmployeeSummarySchema = z.object({
  owner_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  person_a_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  gender: z.string().nullable(),
  count: z.number(),
});
export type SessionsEmployeeSummary = z.infer<typeof SessionsEmployeeSummarySchema>;

/** The group key for an owner; `none` for sessions with no owner. */
export const ownerGroupKey = (ownerId: string | null) => ownerId ?? "none";

function filterQuery(params: CompanySessionsListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (params.q) usp.set("q", params.q);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  if (params.status) usp.set("status", params.status);
  if (params.kind) usp.set("kind", params.kind);
  return usp;
}

/**
 * Every employee with sessions under the current filters, with counts
 * (DES 01-10-2026: "it should always show all users in the list regardless
 * of the page limitation"). One server GROUP BY; owners with none are absent.
 */
export function useCompanySessionsEmployees(
  params: CompanySessionsListParams,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...companySessionsKeys.all, "employees", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "employee");
      const raw = await apiGet<{ employees?: unknown }>(`${sessionEndpoints.list}?${usp}`);
      return z.array(SessionsEmployeeSummarySchema).parse(raw.employees ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const GROUP_BATCH = 25;

/**
 * The sessions of each OPEN group, `batches * GROUP_BATCH` of them, under the
 * same filters (`?owner=`). Load more raises `batches` and refetches the one
 * group.
 */
export function useCompanySessionsGroupRows(
  params: CompanySessionsListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [
        ...companySessionsKeys.all,
        "group",
        key,
        batches,
        filterQuery(params).toString(),
      ],
      placeholderData: (prev: CompanySessionsResponse | undefined) => prev,
      queryFn: async () => {
        const usp = filterQuery(params);
        usp.set("owner", key);
        usp.set("limit", String(batches * GROUP_BATCH));
        usp.set("offset", "0");
        const raw = await apiGet<unknown>(`${sessionEndpoints.list}?${usp}`);
        return CompanySessionsResponseSchema.parse(raw);
      },
    })),
  });
}

function buildQuery(params: CompanySessionsListParams): string {
  const page = params.page ?? 1;
  const limit = params.limit ?? 25;
  const offset = (page - 1) * limit;
  const usp = new URLSearchParams();
  usp.set("limit", String(limit));
  usp.set("offset", String(offset));
  if (params.q) usp.set("q", params.q);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  if (params.employee_id) usp.set("employee_id", params.employee_id);
  if (params.status) usp.set("status", params.status);
  if (params.kind) usp.set("kind", params.kind);
  return usp.toString();
}

function mapCompanySessionDetail(raw: Record<string, unknown>): AdminSessionDetail {
  const parsed = AdminSessionDetailSchema.parse({
    ...raw,
    owner_first_name:
      raw.owner_first_name ?? raw.participant_first_name ?? null,
    owner_last_name: raw.owner_last_name ?? raw.participant_last_name ?? null,
    owner_email: raw.owner_email ?? raw.participant_email ?? null,
    session_kind: raw.session_kind ?? "standard",
  });
  return parsed;
}

export function useCompanySessionsList(
  params: CompanySessionsListParams,
  options?: Partial<UseQueryOptions<CompanySessionsResponse>>,
) {
  return useQuery<CompanySessionsResponse>({
    queryKey: companySessionsKeys.list(params),
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `${sessionEndpoints.list}?${buildQuery(params)}`,
      );
      return CompanySessionsResponseSchema.parse(raw);
    },
    placeholderData: (prev) => prev,
    ...options,
  });
}

/**
 * Cheap nav check — does the company have ANY interpretation session? Fetches a
 * single row and reads the server's total count. Admins bypass the interpretation
 * feature gate on /api/sessions, so this works even when the product is off (a
 * company with past sessions still counts > 0 → keeps the Sessions link). (15-07-2026)
 */
export function useCompanyHasSessions() {
  return useQuery<boolean>({
    queryKey: ["company", "sessions", "has-any"] as const,
    queryFn: async () => {
      const raw = await apiGet<unknown>(`${sessionEndpoints.list}?limit=1&offset=0`);
      const parsed = CompanySessionsResponseSchema.parse(raw);
      return Number(parsed.total ?? 0) > 0;
    },
  });
}

export function useCompanySessionDetail(id: string | null, enabled = true) {
  return useQuery<AdminSessionDetail>({
    queryKey: companySessionsKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(sessionEndpoints.byId(id!));
      const envelope = CompanySessionDetailResponseSchema.safeParse(raw);
      if (envelope.success) {
        return mapCompanySessionDetail(
          envelope.data.session as Record<string, unknown>,
        );
      }
      const direct = AdminSessionDetailSchema.safeParse(raw);
      if (direct.success) return direct.data;
      const wrapped = z
        .object({ session: z.record(z.unknown()) })
        .safeParse(raw);
      if (wrapped.success) {
        return mapCompanySessionDetail(wrapped.data.session);
      }
      throw envelope.error;
    },
  });
}

function makeCompanyDeleteMutation(
  url: (id: string) => string,
): () => ReturnType<typeof useMutation<unknown, Error, string>> {
  return function useDelete() {
    const qc = useQueryClient();
    return useMutation<unknown, Error, string>({
      mutationFn: async (id) => apiDelete<unknown>(url(id)),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: companySessionsKeys.all });
        if (id) {
          void qc.invalidateQueries({
            queryKey: companySessionsKeys.detail(id),
          });
        }
      },
    });
  };
}

export const useDeleteCompanySession =
  makeCompanyDeleteMutation(sessionEndpoints.deleteAll);
export const useDeleteCompanySessionAudio = makeCompanyDeleteMutation(
  sessionEndpoints.deleteAudio,
);
export const useDeleteCompanySessionArtifacts = makeCompanyDeleteMutation(
  sessionEndpoints.deleteArtifacts,
);

export function companySessionsPagination(
  data: CompanySessionsResponse | undefined,
  params: CompanySessionsListParams,
) {
  const limit = params.limit ?? 25;
  const page = params.page ?? 1;
  const total = Number(data?.total ?? 0);
  return {
    page,
    limit,
    total,
    totalPages: Math.max(1, Math.ceil(total / limit)),
  };
}
